import { useEffect, useState } from "react";
import { PencilIcon, TrashIcon, PlusIcon } from "@heroicons/react/outline";
import { useNavigate } from "react-router-dom";
import {
  createAffiliate,
  deleteAffiliate,
  getAffiliates,
  getAffiliatesByEventId,
  updateAffiliate,
} from "../../../../api/affiliates";
import { getTicketTypesByEvent } from "../../../../api/ticketTypesApi";
import ToastContainer from "../../../../components/toastContainer";
import Toast from "../../../../components/toast";
import PageLoader from "../../../../components/pageLoader";
import AffiliateFormModal from "./AffiliateFormModal";
import DeleteConfirmationModal from "../../../../components/deleteModal";

const AffiliateTab = ({ eventId }) => {
  const [showAddModal, setShowAddModal] = useState(false);

  const LIMIT = 10;

  const [affiliates, setAffiliates] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [deleteAffiliateId, setDeleteAffiliateId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalPages = Math.ceil(total / LIMIT);

  const [editingAffiliate, setEditingAffiliate] = useState(null);
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState(null);

  const getPageNumbers = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  };

    const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  const fetchAffiliates = async () => {
    try {
      setLoading(true);
      setError("");

      const offset = (page - 1) * LIMIT;
      const data = await getAffiliatesByEventId(LIMIT, offset, eventId);
      setAffiliates(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail ||
          err?.detail ||
          "Failed to load affiliate links.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAffiliateSubmit = async (info) => {
    try {
      setSaving(true);

      const payload = {
        name: info.name,
        event: eventId,
      };

      if (editingAffiliate) {
        await updateAffiliate(editingAffiliate.id, payload);
      } else {
        await createAffiliate(payload);
      }

      setEditingAffiliate(null);
      setShowAddModal(false);

      fetchAffiliates();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVoucher = async () => {
    try {
      setDeleting(true);
      await deleteAffiliate(deleteAffiliateId);

      setDeleteAffiliateId(null);

      fetchAffiliates();
    } catch (err) {
      alert("Failed to delete the affiliate link");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    fetchAffiliates();
  }, [page]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="grid grid-cols-4 gap-4">
      {/* RIGHT */}
      <div className="bg-[#131C31] border border-white/10 rounded-lg p-4 relative">
        <h3 className="text-slate-400 mb-1">Affiliates</h3>
        <p className="text-slate-600">Coming soon!</p>
      </div>

      <div className="bg-[#131C31] border col-span-3 border-white/10 rounded-lg relative p-2">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-sm text-slate-400">Affiliates Links</p>
          </div>

          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
          >
            <PlusIcon className="w-4 h-4" />
            Create
          </button>
        </div>

        {error && <div className="p-6 text-red-400">{error}</div>}

        {/* Table */}
        <div className="bg-slate-900/60 border border-white/10 rounded-xl">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-white/10 text-center text-xs uppercase tracking-wider text-slate-400">
                  <th className="px-6 py-4">Name</th>
                  <th className="px-6 py-4">Link</th>
                  <th className="px-6 py-4">
                    Total Sales
                    <br />
                    (No VAT)
                  </th>
                  <th className="px-6 py-4">Added by/ at</th>
                  <th className="px-6 py-4">Actions</th>
                </tr>
              </thead>

              <tbody>
                {affiliates.map((a) => (
                  <tr
                    key={a.id}
                    className="border-b text-center border-white/5 last:border-none hover:bg-slate-800/40 transition"
                  >
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-slate-300">
                        {a.name}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-slate-300">
                        <a
                          href={a.link}
                          target="_blank"
                          rel="noreferrer"
                          className="text-pink-500 hover:underline"
                        >
                          View
                        </a>
                      </div>
                    </td>

                    <td className="px-6 py-4 text-sm text-slate-300">
                      {a.total_sales}
                    </td>

                    <td className="px-6 py-4 text-sm text-slate-300">
                      {a.created_by?.first_name} {a.created_by?.last_name} <br/> {formatDate(a.created_at)}
                    </td>

                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setEditingAffiliate(a)}
                          className="p-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-700 transition"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => setDeleteAffiliateId(a.id)}
                          className="p-2 rounded-md text-slate-300 hover:text-red-400 hover:bg-red-500/10 transition"
                          title="Delete company"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty state placeholder (future) */}
          {affiliates.length === 0 && (
            <div className="p-8 text-center text-slate-400">
              No affiliate links were found.
            </div>
          )}
        </div>

        <div className="flex justify-end">
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 text-sm text-slate-400">
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 px-6 py-4 text-sm text-slate-400">
                  {/* Previous */}
                  <button
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                    className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === 1
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                  >
                    Previous
                  </button>

                  {/* Page Numbers */}
                  <div className="flex gap-1">
                    {getPageNumbers().map((pageNumber) => (
                      <button
                        key={pageNumber}
                        onClick={() => setPage(pageNumber)}
                        className={`px-3 py-1 rounded-md border border-white/10 transition
            ${
              page === pageNumber
                ? "bg-pink-600 text-white"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`}
                      >
                        {pageNumber}
                      </button>
                    ))}
                  </div>

                  {/* Next */}
                  <button
                    disabled={page === totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === totalPages
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <AffiliateFormModal
        isOpen={showAddModal || !!editingAffiliate}
        onClose={() => {
          setShowAddModal(false);
          setEditingAffiliate(null);
        }}
        onSubmit={handleAffiliateSubmit}
        loading={saving}
        mode={editingAffiliate ? "edit" : "create"}
        initialData={editingAffiliate}
      />

      <DeleteConfirmationModal
        isOpen={!!deleteAffiliateId}
        description="Are you sure you want to delete this affiliate?  This action cannot be undone."
        onClose={() => setDeleteAffiliateId(null)}
        onConfirm={handleDeleteVoucher}
        loading={deleting}
      />

      <ToastContainer>
        {toast && (
          <div className="toast-enter">
            <Toast
              type={toast.type}
              message={toast.message}
              onClose={() => setToast(null)}
            />
          </div>
        )}
      </ToastContainer>
    </div>
  );
};

export default AffiliateTab;
